// Registers HollowScript as a real Monaco language: tokeniser, live
// diagnostics, completion, hover and a "Format Document" action, all built
// on the pure logic in `src/engine/scripting/monaco/` (tokenisation,
// scope-walking, the capability catalog, diagnostics, completion, hover,
// format - see that directory's own `index.ts` doc comment for why none of
// it imports `monaco-editor` itself). This is the one module in the whole
// feature that is allowed to: it is the thin glue translating those plain
// data shapes into Monaco's actual provider interfaces, and it is only ever
// reached through `HollowScriptEditor.tsx`'s lazy `import()`, so the
// ~98MB `monaco-editor` package is still never pulled into the app's main
// bundle.
//
// Registration is global and idempotent (`registerHollowScriptLanguage`
// guards itself with a module-level flag): Monaco's language/theme/provider
// registries are page-lifetime singletons, not per-editor-instance state, so
// calling this more than once (every `<HollowScriptEditor>` mount calls it
// in `beforeMount`) must never register a second copy of anything - that
// would show every completion/hover result twice.
//
// A project's granted-capability set, by contrast, genuinely is per-editor
// (tier 1's Preview session vs tier 2's published play surface can differ),
// so it is threaded through a small module-level indirection
// (`setActiveHollowScriptCapabilities`) that the currently-mounted editor
// keeps up to date, rather than baked into the provider at registration
// time.

import type * as Monaco from 'monaco-editor';
import type { HighlightClass } from '@hollow/script';
import type { StudioCapabilityName } from '../../../../engine/scripting/capabilityNames.js';
import {
  computeHollowScriptCompletions,
  computeHollowScriptHover,
  computeHollowScriptMarkers,
  computeHollowScriptOutline,
  formatHollowScriptSource,
  tokenizeHollowScriptLine,
  type HollowScriptCompletionKind,
  type OutlineItemKind,
} from '../../../../engine/scripting/monaco/index.js';

export const HOLLOWSCRIPT_LANGUAGE_ID = 'hollowscript';

export type GrantedCapabilitiesResolver = () => ReadonlySet<StudioCapabilityName> | undefined;

/** `undefined` grants every v1 capability - the same default posture
 * `narrowScriptCapabilities`/`createStudioCapabilities` already use
 * elsewhere for "not configured". */
let activeCapabilitiesResolver: GrantedCapabilitiesResolver = () => undefined;

/** Points the shared completion/hover providers at `resolver` for as long
 * as the calling editor is mounted. Returns a cleanup that restores whatever
 * resolver was active before it (correct for the common case of a single
 * live editor, and harmless in the rare case of a nested/sequential one).
 * Call from a `useEffect` keyed on the granted-capabilities value itself, not
 * just on mount - a creator unchecking a capability in Game Setup must reach
 * the very next keystroke's completion list, matching `DockConsole`'s own
 * live `allowedCapabilities` behaviour. */
export function setActiveHollowScriptCapabilities(resolver: GrantedCapabilitiesResolver): () => void {
  const previous = activeCapabilitiesResolver;
  activeCapabilitiesResolver = resolver;
  return () => {
    activeCapabilitiesResolver = previous;
  };
}

const TOKEN_TYPE_FOR_CLASS: Readonly<Record<HighlightClass, string>> = {
  keyword: 'keyword.hollowscript',
  identifier: 'identifier.hollowscript',
  number: 'number.hollowscript',
  string: 'string.hollowscript',
  filter: 'regexp.hollowscript',
  operator: 'operator.hollowscript',
  punctuation: 'delimiter.hollowscript',
  comment: 'comment.hollowscript',
};

/** Tokenisation carries no real per-line state (`tokenize.ts`'s own doc
 * comment: every token is confined to one line), so `IState.equals` always
 * reporting `true` is correct, not a shortcut - it tells Monaco a line's
 * tokens never depend on what came before it, which is true here. */
class StatelessTokenizerState implements Monaco.languages.IState {
  clone(): Monaco.languages.IState {
    return this;
  }
  equals(): boolean {
    return true;
  }
}
const INITIAL_TOKENIZER_STATE = new StatelessTokenizerState();

function createTokensProvider(): Monaco.languages.TokensProvider {
  return {
    getInitialState: () => INITIAL_TOKENIZER_STATE,
    tokenize: (line) => ({
      tokens: tokenizeHollowScriptLine(line).map((token) => ({
        startIndex: token.startIndex,
        scopes: token.kind ? TOKEN_TYPE_FOR_CLASS[token.kind] : '',
      })),
      endState: INITIAL_TOKENIZER_STATE,
    }),
  };
}

const LANGUAGE_CONFIGURATION: Monaco.languages.LanguageConfiguration = {
  // spec.md §1.1: `//` to end of line, no block comment form at all.
  comments: { lineComment: '//' },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"', notIn: ['string', 'comment'] },
    // The filter literal delimiter (spec.md §1.5), HollowScript's embedded
    // foreign-language (HEL) literal - the same syntactic role a regexp
    // literal plays elsewhere, which is also why the tokeniser classifies it
    // as `regexp` (see `TOKEN_TYPE_FOR_CLASS`, mirroring Cascade's own
    // `hollowscript.ts` choice).
    { open: '`', close: '`', notIn: ['string', 'comment'] },
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: '`', close: '`' },
  ],
};

function defineHollowScriptThemes(monaco: typeof Monaco): void {
  // Colours read from soulstack.css's own light/dark custom properties
  // (`--ss-bg`, `--ss-surface`, `--ss-accent`, `--ss-success`, `--ss-danger`,
  // `--ss-text-*`) so the editor reads as part of the surrounding panel
  // rather than a foreign widget dropped into it - Monaco themes need
  // literal colours, not CSS custom properties, so these are a one-time
  // snapshot of those values, not a live binding. The editor background is
  // deliberately the dockview panel background (`--ss-bg`), so there is no
  // visible seam where the editor ends and the panel begins.
  monaco.editor.defineTheme('hollowscript-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword.hollowscript', foreground: 'a78bfa' }, // --ss-accent (dark)
      { token: 'string.hollowscript', foreground: '56b083' }, // --ss-success (dark)
      { token: 'number.hollowscript', foreground: 'e0a860' },
      { token: 'regexp.hollowscript', foreground: 'e0645d' }, // --ss-danger (dark), filter literals
      { token: 'comment.hollowscript', foreground: '76767c', fontStyle: 'italic' }, // --ss-text-3 (dark)
      { token: 'operator.hollowscript', foreground: 'b0b0b8' },
      { token: 'delimiter.hollowscript', foreground: '9a9aa2' },
      { token: 'identifier.hollowscript', foreground: 'e4e4e7' },
    ],
    colors: {
      'editor.background': '#1c1c1c', // --ss-bg (dark)
      'editor.foreground': '#f4f4f5', // --ss-text (dark)
      'editorGutter.background': '#1c1c1c',
      'editorLineNumber.foreground': '#4d4d52',
      'editorLineNumber.activeForeground': '#a6a6ac', // --ss-text-2
      'editor.lineHighlightBackground': '#ffffff08',
      'editor.lineHighlightBorder': '#00000000',
      'editor.selectionBackground': '#a78bfa40',
      'editor.inactiveSelectionBackground': '#a78bfa22',
      'editor.selectionHighlightBackground': '#a78bfa26',
      'editor.wordHighlightBackground': '#ffffff12',
      'editorCursor.foreground': '#c4b5fd', // --ss-accent-strong
      'editorIndentGuide.background1': '#ffffff10',
      'editorIndentGuide.activeBackground1': '#ffffff2a',
      'editorBracketMatch.background': '#a78bfa2a',
      'editorBracketMatch.border': '#a78bfa66',
      'editorWidget.background': '#242424', // --ss-surface
      'editorWidget.border': '#2e2e2e', // --ss-border
      'editorSuggestWidget.background': '#242424',
      'editorSuggestWidget.border': '#2e2e2e',
      'editorSuggestWidget.selectedBackground': '#8a5cf22e', // --ss-accent-soft
      'editorHoverWidget.background': '#242424',
      'editorHoverWidget.border': '#2e2e2e',
      'minimap.background': '#1c1c1c',
      'scrollbarSlider.background': '#ffffff14',
      'scrollbarSlider.hoverBackground': '#ffffff22',
      'scrollbarSlider.activeBackground': '#ffffff2e',
      'editorError.foreground': '#e0645d',
      'editorWarning.foreground': '#d8a54a',
      'focusBorder': '#a78bfa80',
    },
  });
  monaco.editor.defineTheme('hollowscript-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword.hollowscript', foreground: '6444d6' }, // --ss-accent (light)
      { token: 'string.hollowscript', foreground: '2f9e6b' }, // --ss-success (light)
      { token: 'number.hollowscript', foreground: 'a3651b' },
      { token: 'regexp.hollowscript', foreground: 'd0453f' }, // --ss-danger (light)
      { token: 'comment.hollowscript', foreground: '8c8d97', fontStyle: 'italic' }, // --ss-text-3 (light)
      { token: 'operator.hollowscript', foreground: '5a5a63' },
      { token: 'delimiter.hollowscript', foreground: '6b6b74' },
      { token: 'identifier.hollowscript', foreground: '1a1b20' },
    ],
    colors: {
      'editor.background': '#f4f1ea', // --ss-bg (light)
      'editor.foreground': '#1a1b20',
      'editorGutter.background': '#f4f1ea',
      'editorLineNumber.foreground': '#b5b1a6',
      'editorLineNumber.activeForeground': '#565863', // --ss-text-2
      'editor.lineHighlightBackground': '#1a1b2008',
      'editor.lineHighlightBorder': '#00000000',
      'editor.selectionBackground': '#6444d62e',
      'editor.inactiveSelectionBackground': '#6444d618',
      'editor.selectionHighlightBackground': '#6444d61c',
      'editor.wordHighlightBackground': '#1a1b2010',
      'editorCursor.foreground': '#6444d6',
      'editorIndentGuide.background1': '#1a1b2012',
      'editorIndentGuide.activeBackground1': '#1a1b202c',
      'editorBracketMatch.background': '#6444d622',
      'editorBracketMatch.border': '#6444d666',
      'editorWidget.background': '#ffffff', // --ss-surface
      'editorWidget.border': '#e5e0d3', // --ss-border
      'editorSuggestWidget.background': '#ffffff',
      'editorSuggestWidget.border': '#e5e0d3',
      'editorSuggestWidget.selectedBackground': '#6444d61a',
      'editorHoverWidget.background': '#ffffff',
      'editorHoverWidget.border': '#e5e0d3',
      'minimap.background': '#f4f1ea',
      'scrollbarSlider.background': '#1a1b2018',
      'scrollbarSlider.hoverBackground': '#1a1b2028',
      'scrollbarSlider.activeBackground': '#1a1b2038',
      'editorError.foreground': '#d0453f',
      'editorWarning.foreground': '#c08a1e',
      'focusBorder': '#6444d680',
    },
  });
}

const COMPLETION_KIND_MAP: Readonly<Record<HollowScriptCompletionKind, number>> = {
  keyword: 17, // CompletionItemKind.Keyword
  capability: 8, // .Module
  method: 0, // .Method
  function: 1, // .Function
  variable: 4, // .Variable
  constant: 14, // .Constant
  parameter: 4, // .Variable (Monaco has no distinct "parameter" kind)
  record: 6, // .Struct
  import: 8, // .Module
};

function createCompletionProvider(): Monaco.languages.CompletionItemProvider {
  return {
    triggerCharacters: ['.'],
    provideCompletionItems(model, position) {
      const word = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      const items = computeHollowScriptCompletions({
        source: model.getValue(),
        offset: model.getOffsetAt(position),
        lineTextBeforeCursor: model.getLineContent(position.lineNumber).slice(0, position.column - 1),
        grantedCapabilities: activeCapabilitiesResolver(),
      });
      return {
        suggestions: items.map((item) => ({
          label: item.label,
          kind: COMPLETION_KIND_MAP[item.kind] as Monaco.languages.CompletionItemKind,
          detail: item.detail,
          documentation: item.documentation,
          insertText: item.insertText,
          range,
        })),
      };
    },
  };
}

/** `foo.bar`, cursor over `bar`: returns `"foo"`. Mirrors the pure engine
 * layer's own member-access detection (`completion.ts`'s `MEMBER_ACCESS_RE`)
 * so hover and completion agree on what counts as "member position" - kept
 * as a separate small regex here (not imported) since it runs against a
 * `word`-relative slice, a different string shape than completion's
 * cursor-relative one. */
function memberReceiverBefore(lineTextBeforeWord: string): string | undefined {
  const match = /([A-Za-z_][A-Za-z0-9_]*)\.$/.exec(lineTextBeforeWord);
  return match?.[1];
}

function createHoverProvider(): Monaco.languages.HoverProvider {
  return {
    provideHover(model, position) {
      const word = model.getWordAtPosition(position);
      if (!word) return null;
      const lineTextBeforeWord = model.getLineContent(position.lineNumber).slice(0, word.startColumn - 1);
      const info = computeHollowScriptHover({
        source: model.getValue(),
        offset: model.getOffsetAt(position),
        word: word.word,
        memberOf: memberReceiverBefore(lineTextBeforeWord),
        grantedCapabilities: activeCapabilitiesResolver(),
      });
      if (!info) return null;
      return {
        range: {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        },
        contents: info.markdownLines.map((value) => ({ value })),
      };
    },
  };
}

function createFormattingProvider(): Monaco.languages.DocumentFormattingEditProvider {
  return {
    displayName: 'HollowScript',
    provideDocumentFormattingEdits(model) {
      const result = formatHollowScriptSource(model.getValue());
      // A parse error means nothing to format - the same condition already
      // surfaces as a live diagnostic (`updateHollowScriptMarkers`), so a
      // silent no-op here (rather than a second error surface) is correct,
      // not a swallowed failure.
      if (result.formatted === undefined) return [];
      return [{ range: model.getFullModelRange(), text: result.formatted }];
    },
  };
}

/** Monaco's `SymbolKind` values (monaco.languages.SymbolKind) as plain
 * numbers, matching how `COMPLETION_KIND_MAP` avoids a value import too. */
const SYMBOL_KIND_MAP: Readonly<Record<OutlineItemKind, number>> = {
  function: 11, // SymbolKind.Function
  record: 22, // .Struct
  constant: 13, // .Constant
  variable: 12, // .Variable
  uses: 1, // .Module
  import: 1, // .Module
};

/** Powers Monaco's own "Go to Symbol" quick pick (Ctrl/Cmd+Shift+O) with the
 * same outline the Scripts panel shows, so both agree on what a file
 * declares. */
function createDocumentSymbolProvider(): Monaco.languages.DocumentSymbolProvider {
  return {
    displayName: 'HollowScript',
    provideDocumentSymbols(model) {
      return computeHollowScriptOutline(model.getValue()).map((item) => {
        const range: Monaco.IRange = {
          startLineNumber: item.line,
          startColumn: item.column,
          endLineNumber: item.endLine,
          endColumn: item.endColumn,
        };
        return {
          name: item.name,
          detail: item.detail,
          kind: SYMBOL_KIND_MAP[item.kind] as Monaco.languages.SymbolKind,
          tags: [],
          range,
          selectionRange: range,
        };
      });
    },
  };
}

let languageRegistered = false;

/** Registers the `hollowscript` language, its tokeniser, both custom themes,
 * and the completion/hover/format/symbol providers - exactly once, no matter how
 * many times it is called (see this module's own doc comment for why that
 * matters). Call from `@monaco-editor/react`'s `beforeMount`. */
export function registerHollowScriptLanguage(monaco: typeof Monaco): void {
  if (languageRegistered) return;
  languageRegistered = true;

  monaco.languages.register({ id: HOLLOWSCRIPT_LANGUAGE_ID, extensions: ['.hws'], aliases: ['HollowScript', 'hollowscript'] });
  monaco.languages.setLanguageConfiguration(HOLLOWSCRIPT_LANGUAGE_ID, LANGUAGE_CONFIGURATION);
  monaco.languages.setTokensProvider(HOLLOWSCRIPT_LANGUAGE_ID, createTokensProvider());
  defineHollowScriptThemes(monaco);
  monaco.languages.registerCompletionItemProvider(HOLLOWSCRIPT_LANGUAGE_ID, createCompletionProvider());
  monaco.languages.registerHoverProvider(HOLLOWSCRIPT_LANGUAGE_ID, createHoverProvider());
  monaco.languages.registerDocumentFormattingEditProvider(HOLLOWSCRIPT_LANGUAGE_ID, createFormattingProvider());
  monaco.languages.registerDocumentSymbolProvider(HOLLOWSCRIPT_LANGUAGE_ID, createDocumentSymbolProvider());
}

/** Live inline diagnostics (`setModelMarkers`) against the real `check()` -
 * call on every model change, debounced by the caller
 * (`HollowScriptEditor.tsx`), the same discipline Cascade's own
 * `useHollowScriptCheck` debounce hook applies for the identical reason
 * (`check` is synchronous and has no real in-flight call to cancel; a
 * document change within the debounce window should supersede, not queue,
 * the previous pending check). */
export function updateHollowScriptMarkers(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined,
  entryPath?: string,
): void {
  const markers = computeHollowScriptMarkers(model.getValue(), grantedCapabilities, entryPath);
  monaco.editor.setModelMarkers(
    model,
    HOLLOWSCRIPT_LANGUAGE_ID,
    markers.map((marker) => ({
      severity: marker.severity === 'error' ? 8 /* MarkerSeverity.Error */ : 4 /* MarkerSeverity.Warning */,
      message: marker.message,
      startLineNumber: marker.startLineNumber,
      startColumn: marker.startColumn,
      endLineNumber: marker.endLineNumber,
      endColumn: marker.endColumn,
    })),
  );
}
